"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Menu,
  X,
  Settings,
  User,
  CreditCard,
  HelpCircle,
  FileText,
  LogOut,
  LayoutDashboard,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../../lib/authFetch";
import ConfirmModal from "./ConfirmModal";
import { API_BASE } from "../../lib/api";

// Top-tab IA per DESIGN-LANGUAGE.md "Mobile navigation" section.
// Order: Chat / Dashboard / Study / Plan / Review. Chat sits FIRST
// after the brand mark as the always-available conversation surface
// (founder direction, May 13 review). The route is still /maren so
// existing links don't break; only the visible label is "Chat."
// /lira is preserved via a permanent redirect in next.config.ts so
// pre-rebrand bookmarks keep working.
const TOP_TABS = [
  { href: "/maren", label: "Chat" },
  { href: "/dashboard", label: "Dashboard" },
  { href: "/study", label: "Study" },
  { href: "/plan", label: "Plan" },
  { href: "/review", label: "Review" },
];

// Secondary destinations live behind the hamburger / avatar.
// Profile + Billing don't have dedicated routes yet -- link to the
// closest existing surface. Profile deep-links into the settings
// page's profile tab so users land on the right pane immediately
// instead of seeing the same surface as the Settings entry. When
// /profile and /billing are built, update these hrefs.
const SECONDARY_DESTINATIONS = [
  { href: "/settings", label: "Settings", icon: Settings },
  { href: "/settings?tab=profile", label: "Profile", icon: User }, // TODO: /profile when built
  { href: "/pricing", label: "Billing", icon: CreditCard }, // TODO: /billing when built
  { href: "/help", label: "Help", icon: HelpCircle },
  { href: "/disclaimer", label: "Disclaimer", icon: FileText },
];

function isActiveTopLink(href: string, pathname: string): boolean {
  if (href === "/dashboard") return pathname === "/dashboard";
  return pathname === href || pathname.startsWith(href + "/");
}

function deriveInitial(name: string | null | undefined, email: string | null | undefined): string {
  const n = (name ?? "").trim();
  if (n) return n.charAt(0).toUpperCase();
  const e = (email ?? "").trim();
  if (e) return e.charAt(0).toUpperCase();
  return "·";
}

// Tile-style brand lockup: forest-green app-icon tile holding the Maren
// mark, baseline-aligned to a small-caps exam-type badge. The mark PNG
// has the maren-green canvas baked in -- treated as a brand-coloured
// tile, the way Apple Music or Linear use a square symbol. Larger than
// the prior 40px floating mark so the brand reads at first glance.
//
// Sizes: 56px tile on mobile, 64px on sm+; nav shell at h-20 (80px)
// accommodates both with breathing room.
function MarenBrand({ href, label, examType }: { href: string; label: string; examType: string }) {
  return (
    <Link
      href={href}
      className="flex items-center gap-3 select-none group focus:outline-none focus:ring-2 focus:ring-maren-gold/40 focus:ring-offset-2 focus:ring-offset-maren-stone dark:focus:ring-offset-maren-night rounded-md py-1"
      aria-label={label}
    >
      <img
        src="/brand/maren-logo-256.png"
        alt=""
        aria-hidden="true"
        width={256}
        height={256}
        className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-cover shadow-sm ring-1 ring-maren-charcoal/0 dark:ring-maren-sage/15"
      />
      <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-maren-charcoal/70 dark:text-maren-stone/70 leading-none">
        {examType}
      </span>
    </Link>
  );
}

export default function Nav() {
  const pathname = usePathname();
  const router = useRouter();
  const { session, signOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [examType, setExamType] = useState<string>("MCAT");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isPowerUser, setIsPowerUser] = useState(false);
  const avatarMenuRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  const isAuthPage = pathname === "/login" || pathname === "/signup";
  const userEmail = session?.user?.email ?? null;
  const initial = deriveInitial(displayName, userEmail);

  // Founders get an extra Admin entry (the cockpit; the Affiliates console
  // is reachable from there). Everyone else sees the standard set; the link
  // never renders for non-power-users.
  const secondaryDestinations = isPowerUser
    ? [...SECONDARY_DESTINATIONS, { href: "/admin", label: "Admin", icon: LayoutDashboard }]
    : SECONDARY_DESTINATIONS;

  useEffect(() => {
    if (!session) {
      setDisplayName(null);
      setIsPowerUser(false);
      return;
    }
    authFetch(`${API_BASE}/profile`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.exam_type) setExamType(d.exam_type);
        setDisplayName(d?.display_name ?? null);
      })
      .catch(() => {});
    // Founder-only: decides whether the Affiliates link shows in the
    // account menu. 200 with a boolean for everyone; non-founders get false.
    authFetch(`${API_BASE}/affiliate/access`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setIsPowerUser(Boolean(d?.is_power_user)))
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    if (!avatarMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target as Node)) {
        setAvatarMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [avatarMenuOpen]);

  // Close drawer + menu on route change.
  useEffect(() => {
    setDrawerOpen(false);
    setAvatarMenuOpen(false);
  }, [pathname]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDrawerOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [drawerOpen]);

  async function confirmSignOut() {
    setSignOutOpen(false);
    setAvatarMenuOpen(false);
    setDrawerOpen(false);
    await signOut();
    router.push("/login");
  }

  // Shared nav-shell classes. 80px tall (h-20) to fit the stacked
  // brand lockup (mark + Maren wordmark). Sticky, brand border.
  const navShell =
    "bg-maren-stone dark:bg-maren-night border-b border-maren-sage/40 dark:border-maren-charcoal-elevated sticky top-0 z-20";

  // Auth pages get a stripped shell -- just the brand.
  if (isAuthPage) {
    return (
      <nav className={navShell}>
        <div className="max-w-6xl mx-auto px-4 h-20 flex items-center">
          <MarenBrand href="/login" label="Maren" examType={examType} />
        </div>
      </nav>
    );
  }

  return (
    <>
      <nav className={navShell}>
        <div className="max-w-6xl mx-auto px-4 h-20 flex items-center gap-2">
          <MarenBrand
            href={session ? "/dashboard" : "/"}
            label="Maren home"
            examType={examType}
          />

          {session && (
            <>
              {/* Top tabs -- always visible. Below 768px the row scrolls
                  horizontally; tab text labels stay full (no abbreviation
                  games). */}
              <div className="flex-1 min-w-0 overflow-x-auto ml-2 sm:ml-4 scrollbar-none">
                <div className="flex items-center gap-0 h-20">
                  {TOP_TABS.map(({ href, label }) => {
                    const active = isActiveTopLink(href, pathname);
                    return (
                      <Link
                        key={href}
                        href={href}
                        className={`relative h-full inline-flex items-center px-4 text-sm font-semibold whitespace-nowrap transition-colors hover:bg-maren-sage/20 dark:hover:bg-maren-stone/5 ${
                          active
                            ? "text-maren-green dark:text-maren-stone"
                            : "text-maren-charcoal/60 dark:text-maren-stone/60 hover:text-maren-charcoal dark:hover:text-maren-stone"
                        }`}
                      >
                        {label}
                        {active && (
                          <span
                            className="absolute left-0 right-0 -bottom-px h-[3px] bg-maren-gold"
                            aria-hidden="true"
                          />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Right cluster: hamburger (below lg) / avatar dropdown (lg+).
                  Both surface the SAME secondary destinations. */}
              <div className="flex items-center gap-2 ml-auto">
                {/* Hamburger -- visible below 1024px */}
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-maren-charcoal/70 dark:text-maren-stone/70 hover:bg-maren-sage/20 dark:hover:bg-maren-stone/10 focus:outline-none focus:ring-2 focus:ring-maren-gold/40 transition"
                  aria-label="Open menu"
                  aria-expanded={drawerOpen}
                >
                  <Menu size={24} strokeWidth={1.5} aria-hidden="true" />
                </button>

                {/* Avatar dropdown -- visible at lg+ */}
                <div className="relative hidden lg:block" ref={avatarMenuRef}>
                  <button
                    type="button"
                    onClick={() => setAvatarMenuOpen((o) => !o)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-maren-stone shadow-sm hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-maren-gold/40 ${
                      avatarMenuOpen ? "bg-maren-green/80" : "bg-maren-green"
                    }`}
                    aria-label="Account menu"
                    aria-expanded={avatarMenuOpen}
                  >
                    {initial}
                  </button>
                  {avatarMenuOpen && (
                    <div className="absolute right-0 top-12 z-30 w-64 rounded-lg border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone dark:bg-maren-night shadow-lg overflow-hidden">
                      <div className="px-4 py-3 border-b border-maren-sage/30 dark:border-maren-sage/20">
                        <p className="text-sm font-semibold text-maren-charcoal dark:text-maren-stone truncate">
                          {displayName || "Set your name in Settings"}
                        </p>
                        {userEmail && (
                          <p className="text-xs text-maren-charcoal/60 dark:text-maren-stone/60 truncate">
                            {userEmail}
                          </p>
                        )}
                      </div>
                      {secondaryDestinations.map(({ href, label, icon: Icon }) => (
                        <Link
                          key={label}
                          href={href}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-maren-charcoal dark:text-maren-stone hover:bg-maren-sage/20 dark:hover:bg-maren-stone/5 transition"
                        >
                          <Icon size={18} strokeWidth={1.5} aria-hidden="true" className="text-maren-charcoal/60 dark:text-maren-stone/60" />
                          {label}
                        </Link>
                      ))}
                      <div className="border-t border-maren-sage/30 dark:border-maren-sage/20" />
                      <button
                        type="button"
                        onClick={() => setSignOutOpen(true)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-terracotta hover:bg-maren-sage/20 dark:hover:bg-maren-stone/5 transition text-left"
                      >
                        <LogOut size={18} strokeWidth={1.5} aria-hidden="true" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!session && (
            <div className="ml-auto flex items-center gap-1 sm:gap-2">
              {/* "How it works" jumps to the PillarTrio section on the
                  homepage. Using next/link with a hash href lets Next
                  handle both same-page fragment scroll and cross-page
                  navigation (e.g. /pricing → /#how-it-works) uniformly. */}
              <Link
                href="/#how-it-works"
                className="hidden sm:inline-flex px-3 py-1.5 rounded-md text-sm font-medium text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
              >
                How it works
              </Link>
              {/* Visible at EVERY width. This was `hidden sm:inline-flex`,
                  so on a phone there was no Pricing link in the header and
                  no hamburger to replace it -- the only pricing links sat
                  ~7,000px down a ~10,000px landing page. Most premeds
                  browse on a phone, and "what does it cost" is a question
                  you never want to make someone hunt for. "How it works"
                  stays desktop-only: the header has room for one more item
                  at 375px, and price beats explanation at the buy moment. */}
              <Link
                href="/pricing"
                className="inline-flex whitespace-nowrap px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
              >
                Pricing
              </Link>
              <Link
                href="/login"
                className="whitespace-nowrap px-2 sm:px-4 py-1.5 rounded-md text-sm font-medium text-maren-charcoal dark:text-maren-stone hover:text-maren-green dark:hover:text-maren-gold transition"
              >
                Sign in
              </Link>
              <Link
                href="/signup"
                className="whitespace-nowrap px-2.5 sm:px-4 py-1.5 rounded-lg text-sm font-semibold text-maren-stone bg-maren-green hover:bg-maren-green/90 transition shadow-sm"
              >
                Get started
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hamburger slide-in drawer -- mobile + tablet (below lg) */}
      {drawerOpen && session && (
        <>
          <div
            className="fixed inset-0 z-40 bg-maren-charcoal/40 backdrop-blur-sm lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            ref={drawerRef}
            className="fixed top-0 right-0 bottom-0 z-50 w-80 max-w-full bg-maren-stone dark:bg-maren-night shadow-2xl pt-6 pb-8 px-4 overflow-y-auto lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Account menu"
          >
            <div className="flex items-center justify-between mb-4 px-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-charcoal/60 dark:text-maren-stone/60">
                Account
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md text-maren-charcoal/70 dark:text-maren-stone/70 hover:bg-maren-sage/20 dark:hover:bg-maren-stone/10 focus:outline-none focus:ring-2 focus:ring-maren-gold/40 transition"
                aria-label="Close menu"
              >
                <X size={20} strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>

            {/* User block */}
            <div className="px-2 mb-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-maren-green dark:bg-maren-gold text-maren-stone dark:text-maren-charcoal flex items-center justify-center text-sm font-semibold shrink-0">
                {initial}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-maren-charcoal dark:text-maren-stone truncate">
                  {displayName || "Set your name in Settings"}
                </p>
                {userEmail && (
                  <p className="text-xs text-maren-charcoal/60 dark:text-maren-stone/60 truncate">
                    {userEmail}
                  </p>
                )}
              </div>
            </div>

            <div className="border-t border-maren-sage/40 dark:border-maren-sage/20 mb-2" />

            {/* Secondary destinations */}
            <nav aria-label="Secondary navigation">
              {secondaryDestinations.map(({ href, label, icon: Icon }) => (
                <Link
                  key={label}
                  href={href}
                  className="flex items-center gap-3 h-12 px-4 rounded-md text-sm font-semibold text-maren-charcoal dark:text-maren-stone hover:bg-maren-sage/20 dark:hover:bg-maren-stone/5 transition"
                >
                  <Icon size={20} strokeWidth={1.5} aria-hidden="true" className="text-maren-charcoal/60 dark:text-maren-stone/60" />
                  {label}
                </Link>
              ))}
            </nav>

            <div className="border-t border-maren-sage/40 dark:border-maren-sage/20 my-2" />

            <button
              type="button"
              onClick={() => setSignOutOpen(true)}
              className="w-full flex items-center gap-3 h-12 px-4 rounded-md text-sm font-semibold text-terracotta hover:bg-maren-sage/20 dark:hover:bg-maren-stone/5 transition text-left"
            >
              <LogOut size={20} strokeWidth={1.5} aria-hidden="true" />
              Sign Out
            </button>
          </aside>
        </>
      )}

      <ConfirmModal
        open={signOutOpen}
        title="Sign out?"
        message="You'll need to sign back in to access your data."
        confirmLabel="Sign out"
        onConfirm={confirmSignOut}
        onCancel={() => setSignOutOpen(false)}
      />
    </>
  );
}
